<template>
  <div ref="wrapper" class="fx-scroll-view">
    <div :class="direction === 'vertical' ? 'scroll-content' : 'vertical-scroll-content'">
      <div ref="listWrapper">
        <slot></slot>
      </div>
      <slot name="pullup" :pullUpLoad="pullUpLoadOpts" :isPullUpLoad="isPullUpLoad">
        <div class="pullup-wrapper" v-if="pullUpLoadOpts">
          <div class="before-trigger" v-if="!isPullUpLoad">
            <span>{{ pullUpTxt }}</span>
          </div>
          <div class="after-trigger" v-else>
            <loading />
          </div>
        </div>
      </slot>
    </div>
    <slot
      name="pulldown"
      :pullDownRefresh="pullDownRefreshOpts"
      :pullDownStyle="pullDownStyle"
      :beforePullDown="beforePullDown"
      :isPullingDown="isPullingDown"
      :bubbleY="bubbleY"
    >
      <div ref="pulldown" class="pulldown-wrapper" :style="pullDownStyle" v-if="pullDownRefreshOpts">
        <div class="before-trigger" v-if="beforePullDown">
          <bubble :y="bubbleY"></bubble>
        </div>
        <div class="after-trigger" v-else>
          <div v-if="isPullingDown" class="loading">
            <img class="img" src="./loading.gif" />
          </div>
          <div v-else>
            <span>{{ refreshTxt }}</span>
          </div>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import BScroll from '@better-scroll/core';
import Pullup from '@better-scroll/pull-up';
import Pulldown from '@better-scroll/pull-down';
import MouseWheel from '@better-scroll/mouse-wheel';

BScroll.use(Pullup);
BScroll.use(Pulldown);
BScroll.use(MouseWheel);

import Loading from './loading';
import Bubble from './bubble';
import $ from '../util';

const DIRECTION_H = 'horizontal';
const DIRECTION_V = 'vertical';
const PREVENT_DEFAULT_EXCEPTION = {
  tagName: /^(INPUT|TEXTAREA|BUTTON|SELECT)$/,
};
const PULL_DOWN_REFRESH = {
  threshold: 90,
  stop: 40,
  txt: $t('加载成功'),
};
const PULL_UP_LOAD = {
  threshold: 10,
  txt: { more: '', noMore: $t('暂无更多数据') },
};
export default {
  name: 'fx-scroll-view',
  props: {
    data: {
      type: null,
    },
    probeType: {
      type: Number,
      default: 1,
    },
    click: {
      type: Boolean,
      default: true,
    },
    listenScroll: {
      type: Boolean,
      default: false,
    },
    listenBeforeScroll: {
      type: Boolean,
      default: false,
    },
    listenScrollEnd: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: DIRECTION_V,
    },
    scrollbar: {
      type: null,
      default: false,
    },
    pullDownRefresh: {
      type: null,
      default: false,
    },
    pullUpLoad: {
      type: null,
      default: false,
    },
    startY: {
      type: Number,
      default: 0,
    },
    refreshDelay: {
      type: Number,
      default: 20,
    },
    scrollX: {
      type: Boolean,
      default: false,
    },
    scrollY: {
      type: Boolean,
      default: true,
    },
    freeScroll: {
      type: Boolean,
      default: false,
    },
    mouseWheel: {
      type: Boolean,
      default: true,
    },
    preventDefaultException: {
      type: Object,
      default() {
        return PREVENT_DEFAULT_EXCEPTION;
      },
    },
    pullDownStyleProp: {
      type: String,
      default: 'top:-50px',
    },
  },
  data() {
    return {
      timers: [],
      beforePullDown: true,
      isRebounding: false,
      isPullingDown: false,
      isPullUpLoad: false,
      pullUpDirty: true,
      pullDownStyle: this.pullDownStyleProp,
      bubbleY: 0,
    };
  },
  computed: {
    pullUpTxt() {
      const moreTxt = (this.pullUpLoadOpts && this.pullUpLoadOpts.txt && this.pullUpLoadOpts.txt.more) || '';
      const noMoreTxt = (this.pullUpLoadOpts && this.pullUpLoadOpts.txt && this.pullUpLoadOpts.txt.noMore) || '';
      return this.pullUpDirty ? moreTxt : noMoreTxt;
    },
    refreshTxt() {
      return (this.pullDownRefreshOpts && this.pullDownRefreshOpts.txt) || '';
    },
  },
  created() {
    this.pullDownInitTop = -50;
    this.pullDownRefreshOpts = this.pullDownRefresh === true ? PULL_DOWN_REFRESH : this.pullDownRefresh;
    this.pullUpLoadOpts = this.pullUpLoad === true ? PULL_UP_LOAD : this.pullUpLoad;
  },
  mounted() {
    // 这里需要再往后延迟，延迟到slide渲染完毕之后再初始化scroll
    this.$nextTick(() => {
      setTimeout(() => {
        this._initScroll();
      }, 100);
    });
  },
  methods: {
    openPullUp() {
      this.scroll && this.scroll.openPullUp && this.scroll.openPullUp();
    },
    disable() {
      this.scroll && this.scroll.disable();
    },
    enable() {
      this.scroll && this.scroll.enable();
    },
    refresh() {
      this.scroll && this.scroll.refresh();
    },
    scrollBy() {
      this.scroll && this.scroll.scrollBy.apply(this.scroll, arguments);
    },
    scrollTo() {
      this.scroll && this.scroll.scrollTo.apply(this.scroll, arguments);
    },
    scrollToElement() {
      this.scroll && this.scroll.scrollToElement.apply(this.scroll, arguments);
    },
    destroy() {
      this.scroll && this.scroll.destroy();
    },
    forceUpdate(dirty) {
      if (this.pullDownRefreshOpts && this.isPullingDown) {
        this.isPullingDown = false;
        this._reboundPullDown().then(() => {
          this._afterPullDown();
        });
      } else if (this.pullUpLoadOpts && this.isPullUpLoad) {
        this.isPullUpLoad = false;
        this.scroll.finishPullUp();
        this.pullUpDirty = dirty;
        this.refresh();
      } else {
        this.refresh();
      }
    },
    setHeight() {
      this.$refs.listWrapper.style.minHeight = `${$(this.$refs.wrapper).getRect().height + 1}px`;
    },
    _initScroll() {
      if (this.$refs.listWrapper && (this.pullDownRefreshOpts || this.pullUpLoadOpts)) {
        this.$refs.listWrapper.style.minHeight = `${$(this.$refs.wrapper).getRect().height + 1}px`;
      }
      const options = {
        probeType: this.probeType,
        click: this.click,
        scrollY: this.scrollY && (this.freeScroll || this.direction === DIRECTION_V),
        scrollX: this.freeScroll || this.direction === DIRECTION_H,
        scrollbar: this.scrollbar,
        pullDownRefresh: this.pullDownRefreshOpts,
        pullUpLoad: this.pullUpLoadOpts,
        startY: this.startY,
        freeScroll: this.freeScroll,
        mouseWheel: this.mouseWheel,
        preventDefaultException: Object.assign(PREVENT_DEFAULT_EXCEPTION, this.preventDefaultException),
      };
      this.scroll = new BScroll(this.$refs.wrapper, options);
      if (this.listenScroll) {
        this.scroll.on('scroll', pos => {
          this.$emit('scroll', pos);
        });
      }
      if (this.listenScrollEnd) {
        this.scroll.on('scrollEnd', pos => {
          this.$emit('scroll-end', pos);
        });
      }
      if (this.listenBeforeScroll) {
        this.scroll.on('beforeScrollStart', () => {
          this.$emit('beforeScrollStart');
        });
        this.scroll.on('scrollStart', () => {
          this.$emit('scroll-start');
        });
      }
      if (this.pullDownRefreshOpts) {
        this._initPullDownRefresh();
      }
      if (this.pullUpLoadOpts) {
        this._initPullUpLoad();
      }
    },
    _initPullDownRefresh() {
      this.scroll.on('pullingDown', () => {
        this.beforePullDown = false;
        this.isPullingDown = true;
        this.$emit('pullingDown');
      });
      this.scroll.on('scroll', pos => {
        if (this.beforePullDown) {
          this.bubbleY = Math.max(0, pos.y + this.pullDownInitTop);
          this.pullDownStyle = `top:${Math.min(pos.y + this.pullDownInitTop, 10)}px`;
        } else {
          this.bubbleY = 0;
        }
        if (this.isRebounding) {
          this.pullDownStyle = `top:${10 - (this.pullDownRefreshOpts.stop - pos.y)}px`;
        }
      });
    },
    _initPullUpLoad() {
      this.scroll.on('pullingUp', () => {
        this.isPullUpLoad = true;
        this.$emit('pullingUp');
      });
    },
    _reboundPullDown() {
      const { stopTime = 600 } = this.pullDownRefreshOpts;
      return new Promise(resolve => {
        this.timers.push(
          setTimeout(() => {
            this.isRebounding = true;
            this.scroll.finishPullDown();
            resolve();
          }, stopTime)
        );
      });
    },
    _afterPullDown() {
      this.timers.push(
        setTimeout(() => {
          this.pullDownStyle = `top:${this.pullDownInitTop}px`;
          this.beforePullDown = true;
          this.isRebounding = false;
          this.refresh();
        }, this.scroll.options.bounceTime)
      );
    },
  },
  beforeDestroy() {
    this.timers.forEach(timer => {
      clearTimeout(timer);
    });
    this.destroy();
  },
  watch: {
    data() {
      this.timers.push(
        setTimeout(() => {
          this.forceUpdate(true);
        }, this.refreshDelay)
      );
    },
  },
  components: {
    Loading,
    Bubble,
  },
};
</script>

<style lang="less" scoped>
.fx-scroll-view {
  position: relative;
  height: 100%;
  overflow: hidden;
  background-color: #f3f5f8;
}

// 提高列表区块层级，解决被下拉loading遮挡问题
.scroll-content {
  position: relative;
  z-index: 1;
}

.vertical-scroll-content {
  display: inline-block;
}
.pulldown-wrapper {
  position: absolute;
  width: 100%;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all;
  .after-trigger {
    margin-top: 10px;
    color: #aeb6c0;
  }
}

// 注意：这里样式用来占位
.pullup-wrapper {
  color: #aeb6c0;
  .before-trigger {
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    height: 40px;
    width: 100%;
  }
  .after-trigger {
    width: 100%;
    height: 85px;
    box-sizing: border-box;
  }
}
.img {
  display: block;
  width: 20px;
  height: 20px;
}
</style>
